import React, { useContext, useMemo } from 'react'
import * as yup from 'yup'
import { Formik, FormikProps, FormikHelpers } from 'formik'
import { castError } from '@sketch/utils'
import { ErrorHandler } from '@sketch/tracing'

import {
  Button,
  Form,
  Input,
  Modal,
  ModalInjectedProps,
} from '@sketch/components'

import { ToastContext } from '@sketch/toasts'

import {
  useRenameProjectMutation,
  GetProjectsDocument,
  GetProjectsQueryVariables,
} from '@sketch/gql-types'
import { FolderTerm, useFolderTerm } from '@sketch/modules-common'

interface FormValues {
  projectName: string
}

type FormActions = FormikHelpers<FormValues>

type FormOnSubmit = (
  formValues: FormValues,
  formikActions: FormActions
) => Promise<any> | undefined

const renameProjectFormSchema = (folderTerm: FolderTerm) =>
  yup.object({
    projectName: yup
      .string()
      .trim()
      .required(`A ${folderTerm.lower} name is required`),
  })

interface RenameProjectFormProps {
  initialName: string
  onCancel: () => void
  onSubmit: FormOnSubmit
}

const RenameProjectForm: React.FCC<RenameProjectFormProps> = props => {
  const { initialName: projectName, onCancel, onSubmit } = props
  const folderTerm = useFolderTerm()
  const schema = useMemo(
    () => renameProjectFormSchema(folderTerm),
    [folderTerm]
  )

  const handleFormikRender = (formikBag: FormikProps<FormValues>) => {
    const { touched, errors, values, handleBlur, handleChange, isSubmitting } =
      formikBag

    return (
      <Form>
        <Modal.Body>
          <Form.Field
            name="projectName"
            label={`${folderTerm.upper} Name`}
            errorText={touched.projectName ? errors.projectName : undefined}
          >
            <Input
              name="projectName"
              type="text"
              placeholder="Name"
              value={values.projectName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
              autoFocus
            />
          </Form.Field>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isSubmitting} type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button loading={isSubmitting} type="submit" variant="primary">
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    )
  }

  const handleOnSubmit = async (
    values: FormValues,
    formikActions: FormActions
  ) => {
    const trimmedValues = schema.cast(values)

    formikActions.setSubmitting(true)

    try {
      // This is already being validated, but since yup.cast can return
      // undefined we need this extra check.
      const { projectName } = trimmedValues
      projectName && (await onSubmit({ projectName }, formikActions))
    } catch (e) {
      const error = castError(e)
      ErrorHandler.ignore(
        error,
        'Ignore this error the mutation will handle it'
      )
    } finally {
      formikActions.setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{ projectName }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
    >
      {handleFormikRender}
    </Formik>
  )
}

const typeInline = <T extends any>(variables: T) => variables

interface RenameProjectProps extends ModalInjectedProps {
  projectId: string
  projectName: string
  workspaceId: string
}

const RenameProject: React.FCC<RenameProjectProps> = props => {
  const { hideModal, projectId, projectName, workspaceId } = props
  const { showToast } = useContext(ToastContext)
  const folderTerm = useFolderTerm()

  const handleOnComplete = () => {
    showToast(`${folderTerm.upper} was renamed successfully`)
    hideModal()
  }

  const [renameProject] = useRenameProjectMutation({
    redirectErrors: true,
    onCompleted: handleOnComplete,
    onError: 'show-toast',
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
    awaitRefetchQueries: true,
  })

  const onSubmit = ({ projectName: newProjectName }: FormValues) => {
    if (projectName === newProjectName) {
      hideModal()
      return
    }

    return renameProject({
      variables: {
        name: newProjectName,
        projectId,
      },
    })
  }

  return (
    <Modal title={`Rename ${folderTerm.upper}`} onCancel={hideModal}>
      <RenameProjectForm
        onCancel={hideModal}
        onSubmit={onSubmit}
        initialName={projectName}
      />
    </Modal>
  )
}

export default RenameProject
